import {
  AdjustmentsHorizontalIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  QuestionMarkCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { MoonIcon, SunIcon } from "@heroicons/react/24/solid";
import React, { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { FaFireAlt, FaHeart } from "react-icons/fa";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import AvatarCustomizationModal from "../components/AvatarCustomizationModal.tsx";
import LeftSidebar from "../components/LeftSidebar.tsx";
import RightSidebar from "../components/RightSidebar.tsx";
import BottomNav from "../components/BottomNav.tsx";
import TopProfileBar from "../components/TopProfileBar.tsx";
import DotsBackground from "../components/DotsBackground.tsx";
import { coursesData } from "../data/courses/courseData.ts";
import { settingsFAQ } from "../data/ui/uiConfig.ts";
import { UserProfile } from "../types/userData";
import {
  getUserData,
  updateUserProfile,
  getXpProgress,
} from "../utils/userStorage.ts";
import { Instagram, Linkedin } from "lucide-react";

const SettingPage = () => {
  const [currentSection, setCurrentSection] = useState("Pengaturan");
  // Listen for localStorage changes to keep darkMode in sync (e.g. after reload or cross-tab)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "galaxy_aksara_user_data") {
        const data = getUserData();
        setDarkMode(data?.meta?.darkMode ?? false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  // Dark mode state, persistent in user meta
  const [darkMode, setDarkMode] = useState(() => {
    const data = getUserData();
    return data?.meta?.darkMode ?? false;
  });

  // User data from localStorage
  const [userData, setUserData] = useState(
    getUserData() as { profile: UserProfile; [key: string]: any } | null
  );
  const [nickname, setNickname] = useState(userData?.profile.username || "");
  const [avatar, setAvatar] = useState<string | undefined>(
    userData?.profile.photo
  );
  // For modal preview
  const [modalAvatar, setModalAvatar] = useState<string | undefined>(undefined);
  const [modalBg, setModalBg] = useState<string>("#f3f4f6");
  // Get dynamic XP and level data
  const xpData = getXpProgress();
  const userLevel = xpData.level;
  const currentXP = xpData.currentXp;
  const xpToNextLevel = xpData.xpToNextLevel;
  const xpProgressPercent = xpData.progressPercent;
  const [userCourses, setUserCourses] = useState(coursesData);

  // State for delete confirmation modal
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  // Avatar modal state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [selectedBg, setSelectedBg] = useState(
    userData?.profile.avatarBg || "#f3f4f6"
  );
  // FAQ expanded state
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  type ModalState = {
    show: boolean;
    message: string;
    error?: boolean;
  };
  const [exportModal, setExportModal] = useState<ModalState>({
    show: false,
    message: "",
    error: false,
  });
  const [importModal, setImportModal] = useState<ModalState>({
    show: false,
    message: "",
    error: false,
  });
  const [profileModal, setProfileModal] = useState<ModalState>({
    show: false,
    message: "",
    error: false,
  });

  // Load user data and dark mode on component mount
  useEffect(() => {
    const data = getUserData();
    if (data) {
      setUserData(data);
      setNickname(data.profile.username);
      setAvatar(data.profile.photo);
      setDarkMode(data.meta?.darkMode ?? false);
    } else {
      setDarkMode(false);
    }
  }, []);
  // Persist dark mode to user meta (always use latest userData from storage)
  const handleDarkModeToggle = () => {
    setDarkMode((prev) => {
      const newValue = !prev;
      // Use updateMetaField to persist darkMode in UserMeta
      import("../utils/userStorage.ts").then(({ updateMetaField }) => {
        updateMetaField({ darkMode: newValue });
      });
      return newValue;
    });
  };

  // Handle profile update
  const handleUpdateProfile = async () => {
    if (!userData) return;

    try {
      const profileUpdate: Partial<UserProfile> = {
        username: nickname.trim(),
        photo: avatar || userData.profile.photo,
        avatarBg: selectedBg,
      };
      const success = updateUserProfile(profileUpdate);

      if (success) {
        const updatedData = getUserData();
        setUserData(updatedData);
        setProfileModal({
          show: true,
          message: "Profil berhasil disimpan!",
          error: false,
        });
      } else {
        setProfileModal({
          show: true,
          message: "Gagal menyimpan perubahan profil",
          error: true,
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setProfileModal({
        show: true,
        message: "Terjadi kesalahan saat menyimpan profil",
        error: true,
      });
    }
  };

  // Profile update modal popup
  const renderProfileModal = () =>
    profileModal.show && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div
          className={`rounded-xl md:rounded-2xl px-4 py-6 md:px-8 md:py-8 max-w-xs md:max-w-sm w-full mx-2 md:mx-4 border-2 transition-all duration-200
            ${
              darkMode
                ? profileModal.error
                  ? "bg-gray-800 border-red-700"
                  : "bg-gray-800 border-green-700"
                : profileModal.error
                ? "bg-white border-red-500"
                : "bg-white border-green-500"
            }`}
        >
          <div
            className={`mb-4 text-center text-base md:text-lg font-bold transition-all duration-200
              ${
                darkMode
                  ? profileModal.error
                    ? "text-red-400"
                    : "text-green-400"
                  : profileModal.error
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {profileModal.message}
          </div>
          <div className="flex justify-center">
            <button
              className={`px-4 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl font-bold text-sm md:text-base transition-all duration-200
                ${
                  darkMode
                    ? profileModal.error
                      ? "bg-red-700 text-white hover:bg-red-600"
                      : "bg-green-700 text-white hover:bg-green-600"
                    : profileModal.error
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
              onClick={() => setProfileModal({ ...profileModal, show: false })}
              style={{ fontFamily: "Feather Bold" }}
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    );

  const handleRemoveCourse = (idx: number) => {
    // Find the label of the course to be deactivated
    const activeCourses = userCourses.filter((c) => c.active);
    const courseToRemove = activeCourses[idx];
    if (!courseToRemove) return;
    setUserCourses((prev) =>
      prev.map((c) =>
        c.label === courseToRemove.label ? { ...c, active: false } : c
      )
    );
    setDeleteIdx(null);
  };

  const handleExportProgress = () => {
    const data = localStorage.getItem("galaxy_aksara_user_data");
    if (!data) {
      setExportModal({
        show: true,
        message: "No user data found!",
        error: true,
      });
      return;
    }
    try {
      // Get username from userData
      let username = "user";
      try {
        const parsed = JSON.parse(data);
        username = parsed?.profile?.username || "user";
      } catch {}
      // Format date as dd_mm_yyyy
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      const yyyy = now.getFullYear();
      const dateStr = `${dd}_${mm}_${yyyy}`;
      const filename = `galaxy_aksara_${username}_progress_${dateStr}.json`;
      const blob = new Blob([data], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setExportModal({
        show: true,
        message: `Progress berhasil diekspor!`,
        error: false,
      });
    } catch (err) {
      setExportModal({
        show: true,
        message: "Failed to export progress!",
        error: true,
      });
    }
  };

  const handleImportProgress = () => {
    // Create a hidden file input for JSON
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "application/json";
    input.style.display = "none";
    input.onchange = (e) => {
      const file = input.files && input.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const text = event.target?.result as string;
          const parsed = JSON.parse(text);
          localStorage.setItem(
            "galaxy_aksara_user_data",
            JSON.stringify(parsed)
          );
          setImportModal({
            show: true,
            message:
              "Progress berhasil diimpor! Silakan reload halaman untuk melihat perubahan.",
            error: false,
          });
        } catch (err) {
          setImportModal({
            show: true,
            message: "File tidak valid atau gagal diimpor!",
            error: true,
          });
        }
      };
      reader.readAsText(file);
    };
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  // Use imported FAQ data
  const faqs = settingsFAQ;

  // Responsive breakpoints
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const isTablet = useMediaQuery({ minWidth: 768, maxWidth: 1023 });
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLandscape = useMediaQuery({ orientation: "landscape" });

  // console.log("Current Device (Settings Page):");
  // console.log("Mobile:", isMobile);
  // console.log("Tablet:", isTablet);
  // console.log("Desktop:", isDesktop);

  // Export modal popup
  const renderExportModal = () =>
    exportModal.show && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div
          className={`rounded-xl md:rounded-2xl px-4 py-6 md:px-8 md:py-8 max-w-xs md:max-w-sm w-full mx-2 md:mx-4 border-2 transition-all duration-200
            ${
              darkMode
                ? exportModal.error
                  ? "bg-gray-800 border-red-700"
                  : "bg-gray-800 border-green-700"
                : exportModal.error
                ? "bg-white border-red-500"
                : "bg-white border-green-500"
            }`}
        >
          <div
            className={`mb-4 text-center text-base md:text-lg font-bold transition-all duration-200
              ${
                darkMode
                  ? exportModal.error
                    ? "text-red-400"
                    : "text-green-400"
                  : exportModal.error
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {exportModal.message}
          </div>
          <div className="flex justify-center">
            <button
              className={`px-4 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl font-bold text-sm md:text-base transition-all duration-200
                ${
                  darkMode
                    ? exportModal.error
                      ? "bg-red-700 text-white hover:bg-red-600"
                      : "bg-green-700 text-white hover:bg-green-600"
                    : exportModal.error
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
              onClick={() => setExportModal({ ...exportModal, show: false })}
              style={{ fontFamily: "Feather Bold" }}
            >
              Tutup
            </button>
          </div>
        </div>
      </div>
    );

  // Import modal popup
  const renderImportModal = () =>
    importModal.show && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
        <div
          className={`rounded-xl md:rounded-2xl px-4 py-6 md:px-8 md:py-8 max-w-xs md:max-w-sm w-full mx-2 md:mx-4 border-2 transition-all duration-200
            ${
              darkMode
                ? importModal.error
                  ? "bg-gray-800 border-red-700"
                  : "bg-gray-800 border-green-700"
                : importModal.error
                ? "bg-white border-red-500"
                : "bg-white border-green-500"
            }`}
        >
          <div
            className={`mb-4 text-center text-base md:text-lg font-bold transition-all duration-200
              ${
                darkMode
                  ? importModal.error
                    ? "text-red-400"
                    : "text-green-400"
                  : importModal.error
                  ? "text-red-600"
                  : "text-green-600"
              }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {importModal.message}
          </div>
          <div className="flex justify-center">
            {importModal.error ? (
              <button
                className={`px-4 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl font-bold text-sm md:text-base transition-all duration-200
                  ${
                    darkMode
                      ? "bg-red-700 text-white hover:bg-red-600"
                      : "bg-red-500 text-white hover:bg-red-600"
                  }`}
                onClick={() => setImportModal({ ...importModal, show: false })}
                style={{ fontFamily: "Feather Bold" }}
              >
                Tutup
              </button>
            ) : (
              <button
                className={`px-4 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl font-bold text-sm md:text-base transition-all duration-200
                  ${
                    darkMode
                      ? "bg-green-700 text-white hover:bg-green-600"
                      : "bg-green-500 text-white hover:bg-green-600"
                  }`}
                onClick={() => window.location.reload()}
                style={{ fontFamily: "Feather Bold" }}
              >
                Reload
              </button>
            )}
          </div>
        </div>
      </div>
    );

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900" : "bg-gray-100"
      } flex min-h-screen relative`}
    >
      {renderExportModal()}
      {renderImportModal()}
      {renderProfileModal()}
      <DotsBackground darkMode={darkMode} />
      {/* Left Sidebar: desktop shows full, tablet shows compact icon-only, mobile hides */}
      {isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact={false}
        />
      )}
      {isTablet && !isMobile && !isDesktop && (
        <LeftSidebar
          currentSection={currentSection}
          setCurrentSection={setCurrentSection}
          darkMode={darkMode}
          compact={true}
        />
      )}
      <div className="flex-1 flex">
        {/* Top Profile Bar (mobile/tablet only) */}
        {isMobile && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              zIndex: 30,
            }}
          >
            <TopProfileBar darkMode={darkMode}>
              {/* Only show profile section from RightSidebar */}
              <RightSidebar darkMode={darkMode} profileOnly />
            </TopProfileBar>
          </div>
        )}
        <div
          className="flex-1 relative z-10 bg-transparent pt-0 md:pt-0"
          style={
            isMobile
              ? {
                  paddingTop: 64,
                  paddingBottom: 64,
                }
              : {}
          }
        >
          <main className="px-0 md:px-8 pb-10 md:pb-0">
            <div
              className={`mx-auto ${
                isMobile ? "max-w-full px-2 py-4" : "max-w-3xl px-4 py-10"
              }`}
            >
              <h1
                className={`text-4xl text-center font-bold mb-8 ${
                  darkMode ? "text-white" : "text-gray-800"
                }`}
                style={{ fontFamily: "Feather Bold" }}
              >
                Pengaturan
              </h1>

              {/* Profil Section */}
              <section
                className={`mb-6 md:mb-8 rounded-xl md:rounded-2xl md:w-auto border transition-all duration-300 ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="p-4 md:p-8">
                  {/* Header */}
                  <div
                    className={`flex items-center gap-2 mb-4 ${
                      isTablet && !isMobile && !isDesktop
                        ? "md:gap-2 md:mb-4"
                        : "md:gap-3 md:mb-6"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-xl ${
                        darkMode ? "bg-blue-500/20" : "bg-blue-50"
                      }`}
                    >
                      <UserIcon className="w-6 h-6 text-blue-500" />
                    </div>
                    <h2
                      className={`text-xl font-bold ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`}
                      style={{ fontFamily: "Feather Bold" }}
                    >
                      Profil
                    </h2>
                  </div>
                  <div
                    className={`${
                      isTablet && !isMobile && !isDesktop
                        ? "flex flex-col gap-4 items-center"
                        : "flex flex-col md:flex-row gap-4 md:gap-8 items-center"
                    }`}
                  >
                    {/* Avatar Section - Centered both vertically and horizontally */}
                    <div className="flex flex-col items-center justify-center shrink-0 w-full md:w-auto">
                      <div className="text-center">
                        <label
                          className={`block text-sm font-semibold mt-2 ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                          style={{ fontFamily: "Feather Bold" }}
                        >
                          Avatar
                        </label>
                        <div
                          className="relative group cursor-pointer transition-transform hover:scale-105"
                          onClick={() => {
                            setModalAvatar(avatar || "/assets/aksa-1.png");
                            setModalBg(selectedBg);
                            setShowAvatarModal(true);
                          }}
                        >
                          <div
                            className={`rounded-xl border-4 overflow-hidden flex items-center justify-center ${
                              darkMode ? "border-gray-600" : "border-gray-200"
                            } ${
                              isTablet && !isMobile && !isDesktop
                                ? "w-28 h-28"
                                : "w-28 h-28 md:w-40 md:h-40"
                            }`}
                            style={{ background: selectedBg }}
                          >
                            <img
                              src={avatar || "/assets/aksa-1.png"}
                              alt="Avatar"
                              className="w-full h-full rounded-lg object-cover"
                            />
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-300">
                            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                              <PencilIcon className="w-6 h-6 text-white" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Profile Info */}
                    <div className="flex-1 space-y-4 md:space-y-6 w-full">
                      {/* Level info for mobile mode */}
                      {isMobile && userData && (
                        <div className="mt-2 mb-2 flex items-center gap-3 w-full">
                          {/* Level circle (match right sidebar) */}
                          <div className="flex flex-col">
                            <label
                              className={`block text-xs font-semibold mb-1 ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                              style={{ fontFamily: "Feather Bold" }}
                            >
                              Level
                            </label>
                            <div
                              className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500 text-white font-base text-lg"
                              style={{ fontFamily: "Feather Bold" }}
                            >
                              {userLevel}
                            </div>
                          </div>
                          {/* XP and progress bar */}
                          <div className="flex-1 flex flex-col justify-center mt-6">
                            <div
                              className={`text-xs ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                              style={{ fontFamily: "DIN Next Rounded" }}
                            >
                              XP: {currentXP}/{xpToNextLevel}
                            </div>
                            <div
                              className={`w-full rounded-full h-2 my-1 ${
                                darkMode ? "bg-gray-700" : "bg-gray-200"
                              }`}
                            >
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{
                                  fontFamily: "DIN Next Rounded",
                                  width: `${xpProgressPercent}%`,
                                }}
                              ></div>
                            </div>
                            <p
                              className={`text-xs ${
                                darkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                              style={{ fontFamily: "DIN Next Rounded" }}
                            >
                              {xpToNextLevel - currentXP} XP lagi untuk naik ke
                              level {userLevel + 1}
                            </p>
                          </div>
                        </div>
                      )}
                      {/* Username Section */}
                      <div>
                        <label
                          className={`block text-xs font-semibold mb-1 ${
                            darkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                          style={{ fontFamily: "Feather Bold" }}
                        >
                          Username
                        </label>
                        <div
                          className={`flex gap-2 ${
                            isTablet && !isMobile && !isDesktop
                              ? "md:gap-2"
                              : "md:gap-3"
                          }`}
                        >
                          <input
                            type="text"
                            value={nickname}
                            onChange={(e) => setNickname(e.target.value)}
                            className={`flex-1 px-3 py-2 rounded-lg border-2 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                              darkMode
                                ? "bg-gray-700/50 text-white border-gray-600 placeholder-gray-400"
                                : "bg-white text-gray-800 border-gray-300 placeholder-gray-500"
                            }`}
                            placeholder="Masukkan username"
                          />
                          <button
                            onClick={handleUpdateProfile}
                            className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-200 font-semibold transform text-sm ${
                              isTablet && !isMobile && !isDesktop
                                ? "md:px-4 md:py-2 md:text-sm"
                                : "md:px-6 md:py-3 md:text-base"
                            }`}
                          >
                            Simpan
                          </button>
                        </div>
                      </div>

                      {/* Stats Cards */}
                      {userData && (
                        <div
                          className="grid grid-cols-3 md:grid-cols-3 gap-3 md:gap-4"
                          style={{ fontFamily: "Feather Bold" }}
                        >
                          {/* Money */}
                          <div
                            className={`rounded-xl border transition-all duration-200 hover:scale-105 ${
                              darkMode
                                ? "bg-gradient-to-br from-green-900/30 to-green-800/20 border-green-700/30"
                                : "bg-gradient-to-br from-green-50 to-green-100 border-green-200"
                            }`}
                          >
                            <div className={`p-3 text-center`}>
                              <div className="bg-green-500 w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-2">
                                <FaMoneyBill1Wave className="w-6 h-6 text-white" />
                              </div>
                              <div
                                className={`text-xs font-semibold mb-0.5 ${
                                  darkMode ? "text-green-300" : "text-green-600"
                                }`}
                              >
                                Uang
                              </div>
                              <div
                                className={`text-lg font-base ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {userData.meta.money.toLocaleString("id-ID")}
                              </div>
                            </div>
                          </div>
                          {/* Hearts */}
                          <div
                            className={`rounded-xl border transition-all duration-200 hover:scale-105 ${
                              darkMode
                                ? "bg-gradient-to-br from-red-900/30 to-red-800/20 border-red-700/30"
                                : "bg-gradient-to-br from-red-50 to-red-100 border-red-200"
                            }`}
                          >
                            <div className={`p-3 text-center`}>
                              <div className="bg-red-500 w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-2">
                                <FaHeart className="w-6 h-6 text-white" />
                              </div>
                              <div
                                className={`text-xs font-semibold mb-0.5 ${
                                  darkMode ? "text-red-300" : "text-red-600"
                                }`}
                              >
                                Nyawa
                              </div>
                              <div
                                className={`text-lg font-base ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {userData.meta.heart}
                              </div>
                            </div>
                          </div>
                          {/* Streak */}
                          <div
                            className={`rounded-xl border transition-all duration-200 hover:scale-105 ${
                              darkMode
                                ? "bg-gradient-to-br from-orange-900/30 to-orange-800/20 border-orange-700/30"
                                : "bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200"
                            }`}
                          >
                            <div className={`p-3 text-center`}>
                              <div className="bg-orange-500 w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-2">
                                <FaFireAlt className="w-6 h-6 text-white" />
                              </div>
                              <div
                                className={`text-xs font-semibold mb-0.5 ${
                                  darkMode
                                    ? "text-orange-300"
                                    : "text-orange-600"
                                }`}
                              >
                                Streak
                              </div>
                              <div
                                className={`text-lg font-base ${
                                  darkMode ? "text-white" : "text-gray-800"
                                }`}
                              >
                                {userData.meta.streak.current + 1}
                              </div>
                              <div
                                className={`text-xs ${
                                  darkMode
                                    ? "text-orange-400"
                                    : "text-orange-500"
                                }`}
                              >
                                hari
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons Section - Separate at bottom */}
                  <div className="mt-6 md:mt-8 border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-2 md:gap-4">
                      <button
                        className={`px-3 py-2 md:px-4 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-200 transform text-sm md:text-base ${
                          darkMode
                            ? "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                        }`}
                        type="button"
                        onClick={handleExportProgress}
                      >
                        Export Progress
                      </button>
                      <button
                        className={`px-3 py-2 md:px-4 md:py-3 rounded-lg md:rounded-xl font-semibold transition-all duration-200 transform text-sm md:text-base ${
                          darkMode
                            ? "bg-gray-700 text-gray-200 hover:bg-gray-600 border border-gray-600"
                            : "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                        }`}
                        type="button"
                        onClick={handleImportProgress}
                      >
                        Import Progress
                      </button>
                    </div>
                  </div>
                </div>
              </section>

              {/* Preferensi Section */}
              <section
                className={`mb-6 md:mb-8 rounded-xl md:rounded-2xl border transition-all duration-300 ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="p-4 md:p-8">
                  <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                    <div
                      className={`p-2 rounded-xl ${
                        darkMode ? "bg-blue-500/20" : "bg-blue-50"
                      }`}
                    >
                      <AdjustmentsHorizontalIcon className="w-6 h-6 text-blue-500" />
                    </div>
                    <h2
                      className={`text-xl md:text-2xl font-bold ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`}
                      style={{ fontFamily: "Feather Bold" }}
                    >
                      Preferensi
                    </h2>
                  </div>
                  <div className="space-y-4 md:space-y-6">
                    {/* Mode Gelap */}
                    <div className="flex items-center justify-between">
                      <span
                        className={`font-semibold text-base md:text-lg ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                        style={{ fontFamily: "Feather Bold" }}
                      >
                        Mode Gelap
                      </span>
                      <label className="relative inline-flex items-center cursor-pointer w-16 h-9">
                        <input
                          type="checkbox"
                          checked={darkMode}
                          onChange={handleDarkModeToggle}
                          className="sr-only peer"
                        />
                        {/* Background */}
                        <div
                          className={`absolute w-16 h-9 rounded-full transition-colors duration-300
                          ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}
                          style={{ zIndex: 1 }}
                        ></div>
                        {/* Icon */}
                        {darkMode ? (
                          <MoonIcon
                            className="absolute right-9 top-1/2 transform -translate-y-1/2 text-white"
                            width={22}
                            height={22}
                            style={{ zIndex: 2 }}
                          />
                        ) : (
                          <SunIcon
                            className="absolute left-9 top-1/2 transform -translate-y-1/2 text-yellow-400"
                            width={22}
                            height={22}
                            style={{ zIndex: 2 }}
                          />
                        )}
                        {/* Knob */}
                        <div
                          className={`absolute top-1 left-1 w-7 h-7 rounded-full bg-white transition-transform duration-300
                          ${darkMode ? "translate-x-7" : ""}`}
                          style={{ zIndex: 3 }}
                        ></div>
                      </label>
                    </div>

                    {/* Kursus Section */}
                    <div>
                      <div
                        className={`font-semibold text-base md:text-lg mb-2 md:mb-4 ${
                          darkMode ? "text-white" : "text-gray-800"
                        }`}
                        style={{ fontFamily: "Feather Bold" }}
                      >
                        Kursus
                      </div>
                      <div
                        className={`divide-y ${
                          darkMode ? "divide-gray-700" : "divide-gray-200"
                        }`}
                      >
                        {userCourses.filter((course) => course.active)
                          .length === 0 && (
                          <div
                            className={`italic py-4 ${
                              darkMode ? "text-gray-400" : "text-gray-500"
                            }`}
                          >
                            Belum ada kursus.
                          </div>
                        )}
                        {userCourses
                          .filter((course) => course.active)
                          .map((course, idx) => (
                            <div
                              key={course.label}
                              className="flex items-center justify-between py-3"
                            >
                              <div className="flex items-center gap-3 ml-4">
                                <span
                                  className={`font-base ${
                                    darkMode ? "text-white" : "text-gray-800"
                                  }`}
                                  style={{ fontFamily: "Feather Bold" }}
                                >
                                  {course.label}
                                </span>
                              </div>
                              <button
                                className="text-red-500 font-base text-sm hover:text-red-600 transition-colors"
                                style={{ fontFamily: "Feather Bold" }}
                                onClick={() => setDeleteIdx(idx)}
                              >
                                HAPUS
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Help Section */}
              <section
                className={`rounded-xl md:rounded-2xl border transition-all duration-300 ${
                  darkMode
                    ? "bg-gray-800 border-gray-700"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="p-4 md:p-8">
                  <div className="flex items-center gap-2 md:gap-3 mb-4 md:mb-6">
                    <div
                      className={`p-2 rounded-xl ${
                        darkMode ? "bg-green-500/20" : "bg-green-50"
                      }`}
                    >
                      <QuestionMarkCircleIcon className="w-6 h-6 text-green-500" />
                    </div>
                    <h2
                      className={`text-xl md:text-2xl font-bold ${
                        darkMode ? "text-white" : "text-gray-800"
                      }`}
                      style={{ fontFamily: "Feather Bold" }}
                    >
                      Pusat Bantuan
                    </h2>
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    {faqs.map((faq, idx) => (
                      <div
                        key={idx}
                        className={`border rounded-xl overflow-hidden transition-all duration-200 ${
                          darkMode ? "border-gray-600" : "border-gray-200"
                        }`}
                      >
                        <button
                          className={`w-full flex items-center justify-between p-3 md:p-4 text-left transition-colors ${
                            darkMode
                              ? "hover:bg-gray-700/50"
                              : "hover:bg-gray-50"
                          }`}
                          onClick={() =>
                            setExpandedFaq(expandedFaq === idx ? null : idx)
                          }
                        >
                          <div
                            className={`text-base md:text-lg font-bold ${
                              darkMode ? "text-white" : "text-gray-800"
                            }`}
                            style={{ fontFamily: "DIN Next Rounded" }}
                          >
                            {faq.q}
                          </div>
                          {expandedFaq === idx ? (
                            <ChevronUpIcon
                              className={`w-5 h-5 transition-transform ${
                                darkMode ? "text-gray-300" : "text-gray-500"
                              }`}
                            />
                          ) : (
                            <ChevronDownIcon
                              className={`w-5 h-5 transition-transform ${
                                darkMode ? "text-gray-300" : "text-gray-500"
                              }`}
                            />
                          )}
                        </button>
                        {expandedFaq === idx && (
                          <div
                            className={`px-3 md:px-4 pb-3 md:pb-4 border-t ${
                              darkMode ? "border-gray-600" : "border-gray-200"
                            }`}
                          >
                            <div
                              className={`font-semibold mt-2 md:mt-3 text-sm md:text-base ${
                                darkMode ? "text-gray-300" : "text-gray-600"
                              }`}
                            >
                              {Array.isArray(faq.a)
                                ? faq.a.map((part, idx) =>
                                    typeof part === "string" ? (
                                      part.split("\n").map((line, lineIdx) => (
                                        <React.Fragment key={lineIdx}>
                                          {line}
                                          {lineIdx <
                                            part.split("\n").length - 1 && (
                                            <br />
                                          )}
                                        </React.Fragment>
                                      ))
                                    ) : (
                                      <a
                                        key={idx}
                                        href={part.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={
                                          darkMode
                                            ? "text-blue-400 hover:underline"
                                            : "text-blue-600 hover:underline"
                                        }
                                        style={{ fontWeight: 600 }}
                                      >
                                        {part.text}
                                      </a>
                                    )
                                  )
                                : typeof faq.a === "string"
                                ? faq.a.split("\n").map((line, idx) => (
                                    <React.Fragment key={idx}>
                                      {line}
                                      {typeof faq.a === "string" &&
                                        idx < faq.a.split("\n").length - 1 && (
                                          <br />
                                        )}
                                    </React.Fragment>
                                  ))
                                : faq.a}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            </div>
            {isMobile && (
              <footer className="w-full pt-4 md:pt-2 pb-5 md:pb-10 bg-transparent flex flex-col items-center gap-2 z-10 relative">
                <div
                  className="text-gray-500 text-sm font-semibold"
                  style={{ fontFamily: "DIN Next Rounded" }}
                >
                  &copy; 2025 Galaxy Developer
                </div>
                <div className="flex gap-4 mt-1">
                  <a
                    href="https://www.instagram.com/galaxy.developer/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-500 hover:text-pink-500 transition font-semibold text-sm"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/galaxydeveloper"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-500 hover:text-blue-700 transition font-semibold text-sm"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                </div>
              </footer>
            )}
          </main>
        </div>
        {(isDesktop || isTablet) && (
          <div
            style={{
              position: "sticky",
              top: 0,
              right: 0,
              height: "100vh",
              zIndex: 30,
              width: 320,
              background: "transparent",
              pointerEvents: "auto",
              overflowY: "auto",
            }}
          >
            <RightSidebar darkMode={darkMode} />
          </div>
        )}
      </div>
      {/* Bottom Nav (mobile only) */}
      {isMobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            width: "100vw",
            zIndex: 30,
          }}
        >
          <BottomNav
            currentSection={currentSection}
            setCurrentSection={setCurrentSection}
            darkMode={darkMode}
          />
        </div>
      )}
      {/* Delete Confirmation Modal */}
      {deleteIdx !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div
            className={`rounded-xl md:rounded-2xl px-4 py-6 md:px-8 md:py-8 max-w-xs md:max-w-sm w-full mx-2 md:mx-4 ${
              darkMode ? "bg-gray-800 border-2 border-gray-700" : "bg-white"
            }`}
          >
            <div
              className={`mb-4 md:mb-6 text-center text-base md:text-lg font-bold ${
                darkMode ? "text-white" : "text-gray-800"
              }`}
              style={{ fontFamily: "DIN Next Rounded" }}
            >
              Apakah kamu akan menghapus kursus{" "}
              {userCourses.filter((c) => c.active)[deleteIdx]?.label}?
            </div>
            <div
              className="flex justify-center gap-2 md:gap-4"
              style={{ fontFamily: "Feather Bold" }}
            >
              <button
                className={`px-4 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl font-bold text-sm md:text-base transition-all duration-200 transform hover:-translate-y-0.5 ${
                  darkMode
                    ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setDeleteIdx(null)}
              >
                Batalkan
              </button>
              <button
                className="px-4 py-2 md:px-6 md:py-3 rounded-lg md:rounded-xl bg-red-500 text-white font-bold text-sm md:text-base hover:bg-red-600 transition-all duration-200 transform hover:-translate-y-0.5"
                onClick={() => handleRemoveCourse(deleteIdx)}
              >
                Konfirmasi
              </button>
            </div>
          </div>
        </div>
      )}
      <AvatarCustomizationModal
        show={showAvatarModal}
        onClose={() => {
          setModalAvatar(
            avatar || "/assets/profile-characters/edi-transparent.png"
          );
          setModalBg(selectedBg);
          setShowAvatarModal(false);
        }}
        modalAvatar={modalAvatar}
        setModalAvatar={setModalAvatar}
        modalBg={modalBg}
        setModalBg={setModalBg}
        onDone={() => {
          const photoToSave =
            modalAvatar || "/assets/profile-characters/edi-transparent.png";
          setAvatar(photoToSave);
          setSelectedBg(modalBg);
          setShowAvatarModal(false);
        }}
        darkMode={darkMode}
      />
    </div>
  );
};

export default SettingPage;
